/**
 * FlahaSOIL v2 — FlahaCalc export page.
 *
 * Lets the user enter a soil-test id and fetches the FlahaCalc export
 * projection through the active API client. Shows the raw JSON
 * envelope plus the key hydraulic and chemistry-risk fields.
 *
 * This page only PREPARES the export — cross-app transfer to the
 * FlahaCalc application is out of scope.
 */
import {
	Alert,
	Box,
	Button,
	Grid,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useState } from "react";
import type { FlahaCalcExportResponse } from "@flaha/shared-types";

import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";
import { BRAND_TERMS } from "../theme/brand";
import { flahaSoilColors } from "../theme/flahaSoilTheme";

export function FlahaCalcExportPage() {
	const [soilTestId, setSoilTestId] = useState<string>("");
	const [preview, setPreview] = useState<FlahaCalcExportResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	usePageHeader({
		title: BRAND_TERMS.flahaCalcExport,
		subtitle: "Hand off hydraulic and chemistry parameters for irrigation planning",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: BRAND_TERMS.flahaCalcExport },
		],
	});

	const handlePreview = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await getApiClient().getFlahaCalcExport(soilTestId);
			setPreview(result);
		} catch (err) {
			setPreview(null);
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Box>
			<Typography
				color="text.secondary"
				sx={{
					mb: 3,
					pl: 2,
					borderLeft: `3px solid ${flahaSoilColors.blue700}`,
					backgroundColor: flahaSoilColors.blue100,
					borderRadius: 1,
					py: 1.5,
					pr: 2,
				}}
			>
				Prepare a soil test&apos;s hydraulic and chemistry parameters for
				hand-off to FlahaCalc irrigation planning. Enter a soil-test
				reference from one of your projects to preview exactly what will be
				exported before the transfer.
			</Typography>

			<Alert severity="info" sx={{ mb: 3 }}>
				The export uses a stable set of parameters: required fields are
				always present, and any future additions are optional — so your
				irrigation workflow keeps working as the platform evolves.
			</Alert>

			<Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
				<TextField
					label="Soil test reference"
					helperText="Find this on a soil test's detail page"
					size="small"
					value={soilTestId}
					onChange={(e) => setSoilTestId(e.target.value)}
				/>
				<Button
					variant="contained"
					onClick={handlePreview}
					disabled={loading || soilTestId.length === 0}
				>
					{loading ? "Preparing…" : "Preview export"}
				</Button>
			</Stack>

			{error && (
				<Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{preview && <ExportSummary preview={preview} />}
			{preview && <ExportRawJson preview={preview} />}
		</Box>
	);
}

function ExportSummary({ preview }: { preview: FlahaCalcExportResponse }) {
	return (
		<Grid container spacing={2} sx={{ mb: 3 }}>
			<KeyValue label="Texture class" value={preview.textureClass} />
			<KeyValue label="Field capacity" value={preview.fieldCapacity} />
			<KeyValue label="Wilting point" value={preview.wiltingPoint} />
			<KeyValue
				label="Plant available water"
				value={preview.plantAvailableWater}
			/>
			<KeyValue label="Saturation" value={preview.saturation} />
			<KeyValue
				label="Saturated conductivity"
				value={preview.saturatedConductivity}
			/>
			{preview.cec !== undefined && (
				<KeyValue label="CEC" value={preview.cec} />
			)}
			{preview.salinityRisk !== undefined && (
				<KeyValue label="Salinity risk" value={preview.salinityRisk} />
			)}
			{preview.sodiumRisk !== undefined && (
				<KeyValue label="Sodium risk" value={preview.sodiumRisk} />
			)}
		</Grid>
	);
}

function KeyValue({
	label,
	value,
}: {
	label: string;
	value: string | number;
}) {
	return (
		<Grid item xs={12} sm={6} md={4}>
			<Paper variant="outlined" sx={{ p: 2 }}>
				<Typography variant="caption" color="text.secondary">
					{label}
				</Typography>
				<Typography variant="body1">{String(value)}</Typography>
			</Paper>
		</Grid>
	);
}

function ExportRawJson({ preview }: { preview: FlahaCalcExportResponse }) {
	return (
		<Paper
			variant="outlined"
			sx={{
				p: 2,
				bgcolor: "background.default",
				fontFamily: "monospace",
				fontSize: 12,
				overflowX: "auto",
			}}
		>
			<pre style={{ margin: 0 }}>{JSON.stringify(preview, null, 2)}</pre>
		</Paper>
	);
}
